import { useShellCreateAction } from '@ordo/ui/app-shell'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { type Column, DataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { useSenderAddressDefaults, useSenders } from 'hooks/api'
import { Plus, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { isSmsSender, type Sender, senderAddress, type SenderMethod, senderMethod } from 'types/senders'

import { DEFAULT_SMS_REPLY_NOTICE } from '../../lib/sender-options'

type SenderRow = Sender & { displayAddress: string; channel: SenderMethod }
const COLUMNS: Column<SenderRow>[] = [
  {
    key: 'nickname',
    header: 'Friendly name',
    sortable: true,
    filterable: true,
    render: (row) => (
      <Link to={`/admin/senders/${row.id}`} className="text-foreground hover:underline">
        {row.nickname}
        {isSmsSender(row) && row.isDefault ? (
          <Badge variant="secondary" className="ml-2">
            Default
          </Badge>
        ) : null}
      </Link>
    ),
  },
  {
    key: 'displayAddress',
    header: 'From',
    sortable: true,
    filterable: true,
    render: (row) => (
      <div className="flex flex-col">
        <span>{isSmsSender(row) ? row.nickname : row.fromName}</span>
        <span className="text-xs text-muted-foreground">{row.displayAddress}</span>
      </div>
    ),
  },
  {
    key: 'replyToEmail',
    header: 'Reply-to',
    mobile: 'hidden',
    render: (row) =>
      isSmsSender(row)
        ? row.inboundApp === 'schedule'
          ? 'Schedule messages'
          : 'See Inbound settings'
        : row.replyToEmail,
  },
  {
    key: 'verified',
    header: 'Status',
    mobile: 'aside',
    render: (row) =>
      isSmsSender(row) && !row.active ? (
        <Badge variant="secondary">Inactive</Badge>
      ) : row.verified ? (
        <Badge variant="default">Verified</Badge>
      ) : (
        <Badge variant="secondary" className="bg-amber-500/15 text-amber-600 dark:text-amber-400">
          Pending verification
        </Badge>
      ),
  },
]

export function AdminSenders() {
  const [method, setMethod] = useState<SenderMethod>('EMAIL')
  const { data: senders, loading } = useSenders(method)
  const { data: addressDefaults } = useSenderAddressDefaults()
  const addressMissing = addressDefaults && addressDefaults.defaults === null

  const navigate = useNavigate()
  // On phones "New Sender" is the shell's floating + button; the header button is desktop only.
  const newPath = `/admin/senders/new?method=${method}`
  useShellCreateAction({ label: 'New Sender', onCreate: () => navigate(newPath) })

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Senders</h1>
        <Button asChild className="hidden md:inline-flex">
          <Link to={newPath}>
            <Plus className="h-4 w-4" />
            New Sender
          </Link>
        </Button>
      </div>

      <Tabs value={method} onValueChange={(value) => setMethod(value as SenderMethod)} className="mb-4">
        <TabsList>
          <TabsTrigger value="EMAIL">Email</TabsTrigger>
          <TabsTrigger value="SMS">SMS</TabsTrigger>
        </TabsList>
        <TabsContent value={method}>
          {method === 'SMS' ? (
            <p className="mb-4 text-sm text-muted-foreground">
              {DEFAULT_SMS_REPLY_NOTICE} Apps that omit a sender use the configured default.
            </p>
          ) : null}
          {method === 'EMAIL' && addressMissing ? (
            <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div className="text-sm">
                <p className="font-medium text-amber-500">Sender address defaults not configured</p>
                <p className="mt-1 text-muted-foreground">
                  SendGrid requires a physical address on every sender for CAN-SPAM compliance. Set this once on the{' '}
                  <Link to="/admin/integrations" className="underline">
                    Integrations
                  </Link>{' '}
                  page before creating email senders.
                </p>
              </div>
            </div>
          ) : null}

          <DataGrid
            columns={COLUMNS}
            data={(senders ?? []).map((sender) => ({
              ...sender,
              displayAddress: senderAddress(sender),
              channel: senderMethod(sender),
            }))}
            keyField="id"
            pageSize={20}
            loading={loading}
            storageKey={`admin-senders-${method.toLowerCase()}`}
          />
        </TabsContent>
      </Tabs>
    </Page>
  )
}
